import React from 'react';
import PropTypes from 'prop-types';
import BoxAndWhiskerTooltip from './BoxAndWhiskerTooltip.js';
import BoxAndWhiskerDataPoint from './BoxAndWhiskerDataPoint';

class BoxAndWhisker extends React.Component {
    static propTypes = {
        whiskerWidth: PropTypes.number.isRequired,
        boxWidth: PropTypes.number.isRequired,
        boxAndWhiskerDataPoint: PropTypes.instanceOf(BoxAndWhiskerDataPoint),
        x: PropTypes.func.isRequired,
        xValueToSVGCoordinateMapper: PropTypes.func.isRequired,
        yValueToSVGCoordinateMapper: PropTypes.func.isRequired,
        tooltipFormatFunction: PropTypes.func.isRequired,
        chartBoundaries: PropTypes.object.isRequired,
        margins: PropTypes.object.isRequired,
        svgWindowWidth: PropTypes.number.isRequired,
        onMouseLeave: PropTypes.func.isRequired,
        onMouseEnter: PropTypes.func.isRequired,
        whiskerColor: PropTypes.string.isRequired,
    }

    state = {
        'showTooltip': false,
    }

    handleMouseEnter = () => {
        const {
            boxWidth,
            onMouseEnter,
            boxAndWhiskerDataPoint,
            x,
            yValueToSVGCoordinateMapper,
            tooltipFormatFunction,
            svgWindowWidth,
            chartBoundaries,
            margins
        } = this.props;

        // Tooltip X should be at the center of the moused-over boxplot
        let chartWidth = chartBoundaries.right - chartBoundaries.left;
        let tooltipX = this._determineTooltipX({'x': x(boxAndWhiskerDataPoint)});
        let tooltipTriangleDirection;
        if (tooltipX > chartWidth/2.0) {
            tooltipTriangleDirection = 'RIGHT';
        } else {
            tooltipTriangleDirection = 'LEFT';
        }
        tooltipX += margins.left;

        var tooltipY = yValueToSVGCoordinateMapper(boxAndWhiskerDataPoint.median);
        let tooltip = (
            <BoxAndWhiskerTooltip
                tooltipFormatFunction={tooltipFormatFunction}
                tooltipX={tooltipX}
                tooltipY={tooltipY}
                tooltipTriangleDirection={tooltipTriangleDirection}
                boxWidth={boxWidth}
                svgWindowWidth={svgWindowWidth}
                boxAndWhiskerDataPoint={boxAndWhiskerDataPoint}
            />
        );
        this.setState({'showTooltip': true});
        onMouseEnter(tooltip);
    }

    handleMouseLeave = () => {
        this.setState({'showTooltip': false});
        this.props.onMouseLeave();
    }

    _makeComponent() {
        let out = [];
        out = out.concat(this._makeMin());
        out = out.concat(this._makeFirstQuartile());
        out = out.concat(this._makeMedian());
        out = out.concat(this._makeThirdQuartile());
        out = out.concat(this._makeMax());
        out = out.concat(this._makeYouPoint());

        return out;
    }

    _makeMin() {
        const {
            whiskerWidth,
            x,
            boxAndWhiskerDataPoint,
        } = this.props;

        const {
            min,
            primaryColor,
        } = boxAndWhiskerDataPoint;

        let xValue = x(boxAndWhiskerDataPoint);
        let minItem = {'field': 'min',
            'name': 'Min',
            'x': xValue,
            'y0': min,
            'y': 0,
        };
        return [this._makePointOfInterest(minItem, whiskerWidth, primaryColor)];
    }

    _makeFirstQuartile() {
        const {
            whiskerWidth,
            boxWidth,
            x,
            boxAndWhiskerDataPoint,
            whiskerColor
        } = this.props;

        const {
            min,
            firstQuartile,
            primaryColor,
        } = boxAndWhiskerDataPoint;

        let xValue = x(boxAndWhiskerDataPoint);
        let firstQuartileItem = {
            'field': 'firstQuartile',
            'name': '1st Quartile',
            'x': xValue,
            'y0': min,
            'y': firstQuartile - min,
        };

        return [
            this._makeBox(firstQuartileItem, whiskerWidth, whiskerColor),
            this._makePointOfInterest(firstQuartileItem, boxWidth, primaryColor),
        ];
    }

    _makeMedian() {
        const {
            boxWidth,
            x,
            boxAndWhiskerDataPoint,
        } = this.props;

        const {
            firstQuartile,
            median,
            primaryColor,
            secondaryColor,
        } = boxAndWhiskerDataPoint;

        let xValue = x(boxAndWhiskerDataPoint);
        let medianItem = {
            'field': 'median',
            'name': 'Median',
            'x': xValue,
            'y0': firstQuartile,
            'y': median - firstQuartile,
        };
        return [
            this._makeBox(medianItem, boxWidth, secondaryColor),
            this._makePointOfInterest(medianItem, boxWidth, 'white'),
        ];
    }

    _makeThirdQuartile() {
        const {
            boxWidth,
            x,
            boxAndWhiskerDataPoint,
        } = this.props;

        const {
            median,
            thirdQuartile,
            primaryColor,
            secondaryColor,
        } = boxAndWhiskerDataPoint;

        let xValue = x(boxAndWhiskerDataPoint);
        let thirdQuartileItem = {
            'field': 'thirdQuartile',
            'name': '3rd Quartile',
            'x': xValue,
            'y0': median,
            'y': thirdQuartile - median,
        };
        return [
            this._makeBox(thirdQuartileItem, boxWidth, secondaryColor),
            this._makePointOfInterest(thirdQuartileItem, boxWidth, primaryColor),
        ]
    }

    _makeMax() {
        const {
            whiskerWidth,
            x,
            boxAndWhiskerDataPoint,
            whiskerColor,
        } = this.props;

        const {
            thirdQuartile,
            max,
            primaryColor,
        } = boxAndWhiskerDataPoint;

        let xValue = x(boxAndWhiskerDataPoint);
        let maxItem = {
            'field': 'max',
            'name': 'Max',
            'x': xValue,
            'y0': thirdQuartile,
            'y': max - thirdQuartile,
        };
        return [
            this._makeBox(maxItem, whiskerWidth, whiskerColor),
            this._makePointOfInterest(maxItem, whiskerWidth, primaryColor),
        ];
    }

    _makeYouPoint() {
        const {
            x,
            boxAndWhiskerDataPoint,
        } = this.props;

        const {
            thirdQuartile,
            you,
            primaryColor,
        } = boxAndWhiskerDataPoint;

        if (you === null) {
            return [];
        }

        let xValue = x(boxAndWhiskerDataPoint);
        let youItem = {
            'field': 'you',
            'name': 'You',
            'x': xValue,
            'y0': thirdQuartile,
            'y': you - thirdQuartile,
        };
        return [
            this._makePoint(youItem, primaryColor)
        ];
    }

    _makeBox(item, itemWidth, fill) {
        const that = this;
        const {
            yValueToSVGCoordinateMapper,
            pointOfInterestThickness
        } = that.props;
        return (
            <rect
                fill={fill}
                width={itemWidth}
                x={that._determineItemX(itemWidth, item)}
                y={yValueToSVGCoordinateMapper(item.y0 + item.y)}
                height={Math.max(Math.abs(yValueToSVGCoordinateMapper(item.y) - yValueToSVGCoordinateMapper(0)) - pointOfInterestThickness/2.0, 0)}
                key={item.name + "_box"}
            />
        );
    }

    _makePoint(item, fill) {
        const that = this;
        const {
            pointRadius,
            yValueToSVGCoordinateMapper,
            chartBoundaries,
        } = that.props;
        return (
            <g
                key={item.name + "_point"}>
                <circle
                    fill={fill}
                    cx={that._determineItemX(0, item)}
                    cy={Math.max(yValueToSVGCoordinateMapper(item.y0 + item.y), chartBoundaries.top)}
                    r={pointRadius}
                />
                <circle
                    fill={'white'}
                    cx={that._determineItemX(0, item)}
                    cy={Math.max(yValueToSVGCoordinateMapper(item.y0 + item.y), chartBoundaries.top)}
                    r={pointRadius*.5}
                />
            </g>
        );
    }

    _makePointOfInterest(item, itemWidth, fill) {
        const that = this;
        const {
            yValueToSVGCoordinateMapper,
            pointOfInterestThickness
        } = that.props;
        return (
            <rect
                fill={fill || 'black'}
                width={itemWidth}
                x={that._determineItemX(itemWidth, item)}
                y={yValueToSVGCoordinateMapper(item.y0 + item.y) - pointOfInterestThickness/2.0}
                height={pointOfInterestThickness}
                key={item.name + "_poi"}
            />
        );
    }

    _determineItemX(itemWidth, item) {
        const {
            xValueToSVGCoordinateMapper
        } = this.props;
        let itemBandWidth = xValueToSVGCoordinateMapper.bandwidth();
        let initial = xValueToSVGCoordinateMapper(item.x);
        if (!initial && initial!==0) {
            throw new Error('X value was not in expected set of x values');
        }
        let offset = (itemBandWidth - itemWidth) / 2;
        return initial + offset;
    }

    _determineTooltipX(item) {
        const {
            xValueToSVGCoordinateMapper
        } = this.props;
        let itemBandWidth = xValueToSVGCoordinateMapper.bandwidth();
        let initial = xValueToSVGCoordinateMapper(item.x);
        if (!initial && initial!==0) {
            throw new Error('X value was not in expected set of x values');
        }
        return initial + itemBandWidth / 2;
    }

    render() {
        var item = this._makeComponent();

        // Need both for mobile compatibility
        return (
            <g
                onMouseOut={this.handleMouseLeave}
                onMouseOver={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                onMouseEnter={this.handleMouseEnter}
                style={{cursor: 'pointer'}}
                className={'box-plot-box ' + (this.state.showTooltip ? 'box-plot-tooltip-owner' : '')}
            >
                {item}
            </g>
        );
    }
};

export default BoxAndWhisker;
